import { useMemo, useState } from "react";
import type { KeyboardEvent } from "react";

export type IncidentCarouselState = {
  safeIndex: number;
  hasCards: boolean;
  goPrevious: () => void;
  goNext: () => void;
  handleKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
};

export function useIncidentCarouselState(cardCount: number): IncidentCarouselState {
  const [activeIndex, setActiveIndex] = useState(0);
  const hasCards = cardCount > 0;

  const safeIndex = useMemo(() => {
    if (!hasCards) {
      return 0;
    }

    return Math.min(activeIndex, cardCount - 1);
  }, [activeIndex, cardCount, hasCards]);

  function goPrevious() {
    if (!hasCards) {
      return;
    }

    setActiveIndex((current) => (current === 0 ? cardCount - 1 : current - 1));
  }

  function goNext() {
    if (!hasCards) {
      return;
    }

    setActiveIndex((current) => (current === cardCount - 1 ? 0 : current + 1));
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (!hasCards) {
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goPrevious();
      return;
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      goNext();
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(cardCount - 1);
    }
  }

  return {
    safeIndex,
    hasCards,
    goPrevious,
    goNext,
    handleKeyDown,
  };
}
